import Image from 'next/image';

import { Box, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import {
  type BackToSchool2026SaleVariant,
  BTS_2026_SALE_VARIANTS,
  getBts2026SaleCtaHref,
} from '~/features/promotions/constants/back-to-school-2026-sale';

import { BackToSchool2026HeadlineLines } from './back-to-school-2026-headline';

export type BackToSchool2026PromoCardProps = {
  variant: BackToSchool2026SaleVariant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  imagePriority?: boolean;
  onClick?: () => void;
};

/**
 * Homepage impact carousel card for Back to School 2026 (Figma 1856:2049 desktop, 1856:2091
 * mobile). Unlike the Summer 2026 card there is no "Ends soon" eyebrow, the headline stacks a gold
 * line 1 over a white line 2 with underline art on the price, and the CTA is navy-on-gold.
 */
export function BackToSchool2026PromoCard({
  variant,
  promoCode,
  imagePriority,
  onClick,
}: BackToSchool2026PromoCardProps) {
  const config = BTS_2026_SALE_VARIANTS[variant];
  const ctaHref = getBts2026SaleCtaHref(promoCode);
  const cardId = `back-to-school-2026-${variant}-sale-title`;

  return (
    <PromoCard
      backgroundColor={config.homepageBackgroundColor}
      // An omitted slot falls back to PromoCard's default star eyebrow, which this design drops.
      eyebrowSlot={<></>}
      id={cardId}
      sectionGap={6}
      backgroundImageSlot={
        <>
          <Box
            bottom={0}
            display={{ base: 'block', md: 'none' }}
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
            top={0}
            zIndex={0}
          >
            <Image
              alt=''
              loading='eager'
              priority={imagePriority}
              sizes='100vw'
              src={config.homepageBgMobile}
              style={{
                objectFit: config.homepageMobileObjectFit,
                objectPosition: config.homepageMobileObjectPosition,
              }}
              fill
            />
          </Box>
          <Box
            bottom={0}
            display={{ base: 'none', md: 'block' }}
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
            top={0}
            zIndex={0}
          >
            <Image
              alt=''
              loading='eager'
              priority={imagePriority}
              sizes='(max-width: 768px) 100vw, 768px'
              src={config.homepageBg}
              style={{ objectFit: config.homepageObjectFit, objectPosition: config.homepageObjectPosition }}
              fill
            />
          </Box>
        </>
      }
      buttonSlot={
        <ButtonLink
          linkProps={{ href: ctaHref }}
          buttonProps={{
            _hover: { opacity: 0.9 },
            alignItems: 'center',
            backgroundColor: config.homepageCtaColor,
            color: config.homepageCtaTextColor,
            display: 'inline-flex',
            fontSize: '15px',
            fontWeight: '500',
            height: 'auto',
            justifyContent: 'center',
            lineHeight: '17px',
            paddingX: '24px',
            paddingY: '14px',
            position: 'relative',
            variant: 'unstyled',
            zIndex: 1,
            width: {
              base: '100%',
              md: '152px',
            },
            onClick,
          }}
        >
          {config.homepageCtaLabel}
        </ButtonLink>
      }
      contentSlot={
        <Box>
          <Heading
            as='h2'
            color={config.homepageTitleColor}
            fontWeight='600'
            id={cardId}
            letterSpacing='-0.01em'
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.14',
              md: '1.1',
            }}
          >
            <LinkOverlay href={ctaHref} onClick={onClick}>
              <BackToSchool2026HeadlineLines config={config} />
            </LinkOverlay>
          </Heading>
          <Text color={config.homepageSubtitleColor} lineHeight={1.5}>
            {config.homepageSubtitle}
          </Text>
        </Box>
      }
      padding={{
        base: '32px',
        md: '48px 48px 56px 48px',
      }}
    />
  );
}
